/**
 * PATH: apps/qa/src/pages/area-execution/EvidenceGallery.tsx
 * PURPOSE: Evidence photo gallery and viewer modal for checklist items
 */

import { QaPhotoGallery, QaPhotoLightbox, type QaGalleryPhoto } from '@quotech/ui'
import { Camera, FileText, Plus } from 'lucide-react'
import type { Evidence } from '@/types/qa'

interface EvidenceGalleryProps {
  evidence: Evidence[]
  onAddEvidence: () => void
  onViewEvidence: (evidence: Evidence) => void
  onDelete?: (evidenceId: string) => void | Promise<void>
}

function photoSrc(ev: Evidence): string | null {
  return ev.display_url ?? ev.asset_url ?? null
}

function toGalleryPhoto(ev: Evidence): QaGalleryPhoto {
  return {
    id: ev.id,
    src: photoSrc(ev),
    alt: ev.description || 'QA photo',
    caption: ev.description,
  }
}

export function EvidenceGallery({ evidence, onAddEvidence, onViewEvidence, onDelete }: EvidenceGalleryProps) {
  if (evidence.length === 0) {
    return (
      <button onClick={onAddEvidence}
        className="w-full py-3 rounded-lg border-2 border-dashed border-border hover:border-primary/50 transition-colors flex items-center justify-center gap-2 text-muted-foreground hover:text-primary">
        <Camera className="w-4 h-4" /><span className="text-sm">Add Evidence Photo</span>
      </button>
    )
  }

  const photos = evidence.filter((ev) => ev.type === 'photo' || ev.type === 'video' || ev.type === 'signature')
  const other = evidence.filter((ev) => ev.type !== 'photo' && ev.type !== 'video' && ev.type !== 'signature')

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground">Evidence ({evidence.length})</span>
        <button onClick={onAddEvidence} className="text-xs text-primary hover:underline flex items-center gap-1">
          <Plus className="w-3 h-3" />Add More
        </button>
      </div>
      <QaPhotoGallery
        photos={photos.map(toGalleryPhoto)}
        onDelete={onDelete ? (id) => void onDelete(id) : undefined}
        onOpen={(index) => onViewEvidence(photos[index])}
      />
      {other.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {other.map((ev) => (
            <button
              key={ev.id}
              type="button"
              onClick={() => onViewEvidence(ev)}
              className="inline-flex items-center gap-1 rounded-lg border border-border bg-muted px-2 py-1 text-xs"
            >
              <FileText className="w-4 h-4 text-muted-foreground" />
              <span className="truncate">{ev.description || ev.note_text || 'Attachment'}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}

export function EvidenceViewer({ evidence, onClose }: { evidence: Evidence; onClose: () => void }) {
  const photos = [toGalleryPhoto(evidence)]
  return (
    <QaPhotoLightbox
      photos={photos}
      index={0}
      onIndexChange={() => {}}
      onClose={onClose}
    />
  )
}
